import { Box, Stack, useDisclosure } from '@chakra-ui/react'
import type { InsuranceParamsPropertyDto, Offer } from 'BrokerTsLib/src/dtos'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import { InsuranceQuoteGroup } from '@/components/InsuranceQuoteGroup'
import { Parameters } from '@/components/Parameters/Parameters'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useOffersPropertyContext } from '@/context/OffersPropertyProvider'
import { usePrintContext } from '@/context/PrintContext'
import { useQuotes } from '@/context/QuotesContext'
import { useSidebar } from '@/context/SidebarContext'
import { useApp } from '@/hooks/useApp'
import { LeftOffset } from '@/layouts/components/PageWrapper'
import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { useScrollToPageSection } from '@/layouts/components/Sidebar/Sidebar'
import { Product, Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import { OfferPackageGrid } from '../../offers/grid/package/OfferPackageGrid'


export const Step2SidebarIds = {
  Parameters: 'parameters',
  Quotes: 'quotes',
} as const
export type Step2SidebarIds = typeof Step2SidebarIds[keyof typeof Step2SidebarIds]

export const Step2 = () => {
  const quotesRef = useRef(null)
  const { product } = useApp()

  const { pageSections, setPageSections } = useSidebar()
  const scrollToPageSection = useScrollToPageSection()
  const { insuranceParams, refetchInsuranceParams, isDisabled } = useInsuranceParams()
  const insuranceParamsProperty = insuranceParams as InsuranceParamsPropertyDto

  const {
    isOpen: isOpenParameters,
    onOpen: onOpenParameters,
    onClose: onCloseParameters,
    onToggle: onToggleParameters,
  } = useDisclosure({ defaultIsOpen: !insuranceParams?.generalParams.existsOffers })

  const {
    refetchOffers: getOffers,
    offers,
    showAll,
    setShowAll: changeShowAll,
    filtered,
    isLoading,
    calculatingOffersIds,
  } = useOffersPropertyContext()

  const {
    setShowFilter,
    onCalculationStateChange,
    calculationState,
    setCalculationState,
  } = useQuotes()

  const { setWrapperMarginTop } = useSidebar()
  const { printMode } = usePrintContext()
  const [highlightedQuotesList, setHighlightedQuotesList] = useState<Offer[]>([])
  const [ratingChanged, setRatingChanged] = useState(new Date())
  const [shouldScrollToQuotes, setShouldScrollToQuotes] = useState(false)

  const isCalculatingAny = isLoading || (calculatingOffersIds?.length ?? 0) > 0

  const isHiddenParameters = printMode

  const handleState = useCallback(
    (isCalculated: boolean, isCalculating: boolean) => {
      setShowFilter(isCalculated)

      setCalculationState({
        isCalculated,
        isCalculating,
      })
      onCalculationStateChange?.({
        isCalculated,
        isCalculating,
      })
    }, [])

  // ziskani offers na page load, pokud nejake existuji
  const [firstLoad, setFirstLoad] = useState(true)
  useEffect(() => {
    if (firstLoad) setFirstLoad(false)
    else return

    // offery ziskavam na page load pouze pokud existuji nejake
    if (!insuranceParams?.generalParams.existsOffers) return

    handleState(false, true)

    setTimeout(() => {
      scrollToQuotes()
    }, 1000)

    getOffers({})
  }, [insuranceParams?.generalParams.existsOffers, firstLoad])

  // pri scrollu na offers jenom nastavim priznak a o samotny scroll se postara useEffect
  const scrollToQuotes = () => {
    setShouldScrollToQuotes(true)
  }

  // scrollovani na kalkulace
  useEffect(() => {
    // Chceme scroll až po zobrazení sekce nabídek
    if (
      shouldScrollToQuotes &&
      insuranceParams?.generalParams.existsOffers &&
      (calculationState.isCalculating ||
        (calculationState.isCalculated && filtered.length == 0))
    ) {
      // S timeoutem, aby se stihly dokončit animace a změny layoutu.
      setTimeout(() => scrollToPageSection(Step2SidebarIds.Quotes), 500)
      setShouldScrollToQuotes(false)
    }
  }, [
    shouldScrollToQuotes,
    insuranceParams?.generalParams.existsOffers,
    calculationState.isCalculating,
    calculationState.isCalculated,
    filtered.length,
  ])

  // automaticka zmena stavu na zaklade dat
  useEffect(() => {
    // do offers se dostane az po kalkulaci, nebo kdyz je nabidka uz sjednana
    handleState(offers.length > 0, isCalculatingAny)
  }, [offers.length, isCalculatingAny])

  // ziskanni / zajisteni vytvoreni offers
  const handleCalculateOffers = () => {
    onCloseParameters()
    scrollToQuotes()

    return getOffers({ force: true }).then(() => {
      setShowFilter(true)

      // aby se pripadne nove varianty zobrazily ve filteru a aby paramsy uz na sobe mely existsOffers
      if (!insuranceParams?.generalParams.existsOffers) refetchInsuranceParams()
    })
  }

  const handleAddNewVariant = () => {
    onOpenParameters()
    scrollToPageSection(Step2SidebarIds.Parameters)
  }

  useEffect(() => {
    const newHightlightedQuotes = filtered.filter(
      (quote) =>
        quote.stars! > 0 ||
        quote.offerId == insuranceParams?.generalParams.selectedOfferId,
    )

    setHighlightedQuotesList(newHightlightedQuotes)
  }, [filtered, ratingChanged])

  useEffect(() => {
    if (calculationState.isCalculated) {
      const quotesSection = document.getElementById(Step2SidebarIds.Quotes)
      const marginTop = quotesSection?.getBoundingClientRect().top
      if (marginTop)
        setWrapperMarginTop?.(marginTop)
    }
  }, [calculationState.isCalculated, quotesRef, setWrapperMarginTop])

  // seskupování podle domácnost / nemovitost / domácnost + nemovitost -> ne podle jednotlivých variant
  const variantsHoushold = useMemo(() => insuranceParamsProperty.property.variants?.filter(v => v.variantParts && v.variantParts.some(vp => vp.risk == Risk.PROPERTY_TYPE) && v.variantParts.filter(vp => vp.risk == Risk.PROPERTY_TYPE).every(vp => vp.riskVariant == RiskVariant.Household)), [insuranceParamsProperty.property.variants])
  const variantsRealty = useMemo(() => insuranceParamsProperty.property.variants?.filter(v => v.variantParts && v.variantParts.some(vp => vp.risk == Risk.PROPERTY_TYPE) && v.variantParts.filter(vp => vp.risk == Risk.PROPERTY_TYPE).every(vp => vp.riskVariant == RiskVariant.Realty)), [insuranceParamsProperty.property.variants])
  const variantsHousholdAndRealty = useMemo(() => insuranceParamsProperty.property.variants?.filter(v => v.variantParts && v.variantParts.some(vp => vp.risk == Risk.PROPERTY_TYPE) && v.variantParts.filter(vp => vp.risk == Risk.PROPERTY_TYPE).every(vp => vp.riskVariant == RiskVariant.HouseholdAndRealty)), [insuranceParamsProperty.property.variants])
  const variantsResponsibility = useMemo(() => insuranceParamsProperty.property.variants?.filter(v => v.variantParts && v.variantParts.some(vp => vp.risk == Risk.RESPONSIBILITY || vp.risk == Risk.RESPONSIBILITY_REALESTATE)), [insuranceParamsProperty.property.variants])

  // registrace sekcí
  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (!isHiddenParameters) {
      array.push({
        id: Step2SidebarIds.Parameters,
        sectionTitle: 'Parametry pojištění',
        index: i++,
        children: (
          <CustomWaypoint sectionId={Step2SidebarIds.Parameters}>
            <Box ml={LeftOffset}>
              <Parameters
                isCollapsed={!isOpenParameters}
                id={Step2SidebarIds.Parameters}
                cardHeaderProps={{
                  cursor: 'pointer',
                  onClick: onToggleParameters,
                }}
                calculationState={calculationState}
                onCalculateOffers={handleCalculateOffers}
              />
            </Box>
          </CustomWaypoint>
        ),
      })
    }

    if (insuranceParams?.generalParams.existsOffers)
      array.push({
        id: Step2SidebarIds.Quotes,
        sectionTitle: calculationState.isCalculating
          ? 'Kalkuluji...'
          : 'Kalkulace pojištění',
        isDisabled: !calculationState.isCalculated || isDisabled,
        index: i++,
        children: (
          <CustomWaypoint sectionId={Step2SidebarIds.Quotes}>
            <Stack sx={{ gap: 8 }}>
              <InsuranceQuoteGroup
                id={Step2SidebarIds.Quotes}
                ref={quotesRef}
                groupHeaderProps={{
                  visibleCount: filtered.length,
                  ml: LeftOffset,
                  totalCount: offers.length,
                  enableAddVariant: !isDisabled && !printMode,
                  sectionTitle: calculationState.isCalculating
                    ? 'Kalkuluji...'
                    : 'Kalkulace pojištění',
                  onShowAllQuotes: () => changeShowAll(!showAll),
                  calculationState: calculationState,
                  onAddNewVariant: () => {
                    handleAddNewVariant()
                  },
                }}
                groupFooterProps={{
                  enableAddVariant: !isDisabled,
                  onAddNewVariant: () => {
                    handleAddNewVariant()
                  },
                }}
              >
                {/* {calculationState.isCalculated && <OfferPackageGrid />} */}
                {calculationState.isCalculated && variantsHoushold && variantsHoushold.length > 0 &&
                  <OfferPackageGrid key={RiskVariant.Household} variants={variantsHoushold} icon='household' />
                }

                {calculationState.isCalculated && variantsRealty && variantsRealty.length > 0 &&
                  <OfferPackageGrid key={RiskVariant.Realty} variants={variantsRealty} icon='property' />
                }

                {calculationState.isCalculated && variantsHousholdAndRealty && variantsHousholdAndRealty.length > 0 &&
                  <OfferPackageGrid key={RiskVariant.HouseholdAndRealty} variants={variantsHousholdAndRealty} icon='householdProperty' />
                }

                {calculationState.isCalculated && product == Product.LiabilityInsurance && variantsResponsibility && variantsResponsibility.length > 0 &&
                  <OfferPackageGrid variants={variantsResponsibility} icon='responsibility' />
                }
              </InsuranceQuoteGroup>
            </Stack>
          </CustomWaypoint>
        ),
      })

    setPageSections(array)
  }, [
    calculationState.isCalculating,
    calculationState.isCalculated,
    filtered,
    insuranceParams?.generalParams.existsOffers,
    isOpenParameters,
    variantsHoushold,
    variantsRealty,
    variantsHousholdAndRealty,
    variantsResponsibility,
  ])

  return (
    <Stack sx={{ gap: 8 }}>
      {pageSections.map((x) => (
        <React.Fragment key={x.id}>{x.children}</React.Fragment>
      ))}
    </Stack>
  )
}
